/**
 * Keeping a pet log honest. Two separate jobs:
 *
 * 1. `normalizeLog` cleans a log into a canonical form that does not depend on the order the events arrived in: it
 *    sorts, removes duplicates and impossible values, merges overlapping sessions, and turns care that was done while
 *    the log says the app was closed into a short session (care means the app was open at that moment).
 * 2. The `record*` helpers add events while the game runs. They protect against a device clock that moves backwards:
 *    a new event is never earlier than anything already recorded (negative time steps are clamped to zero).
 *
 * All functions return new objects; they never change their input.
 */
import type { Session } from "./effective-time";
import { METER_IDS, type CareEvent, type MeterId, type PetLog } from "./types";

const meterOrder = (m: MeterId) => METER_IDS.indexOf(m);

/** The longest refill a care action can have (10 minutes). Anything longer in a log is clamped to this. */
export const MAX_FILL_MS = 10 * 60_000;

/** A usable fill (a positive finite number, clamped), or undefined for "instant". */
export function cleanFill(fill: unknown): number | undefined {
  if (typeof fill !== "number" || !Number.isFinite(fill) || fill <= 0) return undefined;
  return Math.min(fill, MAX_FILL_MS);
}

function mergeSessions(sessions: readonly Session[]): Session[] {
  const sorted = sessions
    .filter((s) => Number.isFinite(s.openAt) && Number.isFinite(s.lastSeenAt))
    .map((s) => ({ openAt: s.openAt, lastSeenAt: Math.max(s.openAt, s.lastSeenAt) }))
    .sort((a, b) => a.openAt - b.openAt || a.lastSeenAt - b.lastSeenAt);
  const merged: Session[] = [];
  for (const s of sorted) {
    const last = merged[merged.length - 1];
    if (last && s.openAt <= last.lastSeenAt) last.lastSeenAt = Math.max(last.lastSeenAt, s.lastSeenAt);
    else merged.push({ ...s });
  }
  return merged;
}

export function normalizeLog(log: PetLog): PetLog {
  const care: CareEvent[] = log.care
    .filter((c) => Number.isFinite(c.at) && c.at >= log.hatchedAt && meterOrder(c.meter) >= 0)
    .map((c): CareEvent => {
      if (c.stop === true) return { at: c.at, meter: c.meter, stop: true };
      const fill = cleanFill(c.fill);
      return fill === undefined ? { at: c.at, meter: c.meter } : { at: c.at, meter: c.meter, fill };
    })
    .sort((a, b) => a.at - b.at || meterOrder(a.meter) - meterOrder(b.meter));
  const unique = care.filter((c, i) => i === 0 || c.at !== (care[i - 1] as CareEvent).at || c.meter !== (care[i - 1] as CareEvent).meter);

  let sessions = mergeSessions(log.sessions);
  const outside = unique.filter((c) => !sessions.some((s) => c.at >= s.openAt && c.at <= s.lastSeenAt));
  if (outside.length > 0) sessions = mergeSessions([...sessions, ...outside.map((c) => ({ openAt: c.at, lastSeenAt: c.at }))]);

  return { ...log, care: unique, sessions };
}

/** The latest time anything was recorded in the log. */
export function highWaterMark(log: PetLog): number {
  let high = log.hatchedAt;
  for (const c of log.care) if (Number.isFinite(c.at)) high = Math.max(high, c.at);
  for (const s of log.sessions) {
    if (Number.isFinite(s.openAt)) high = Math.max(high, s.openAt);
    if (Number.isFinite(s.lastSeenAt)) high = Math.max(high, s.lastSeenAt);
  }
  return high;
}

export function recordCare(log: PetLog, meter: MeterId, now: number, fill?: number): PetLog {
  const at = Math.max(now, highWaterMark(log));
  const clean = cleanFill(fill);
  const event: CareEvent = clean === undefined ? { at, meter } : { at, meter, fill: clean };
  return { ...log, care: [...log.care, event] };
}

/** The pet woke up: a sleeping refill of this meter stops where it is (see `CareEvent.stop`). */
export function recordWake(log: PetLog, meter: MeterId, now: number): PetLog {
  const at = Math.max(now, highWaterMark(log));
  return { ...log, care: [...log.care, { at, meter, stop: true }] };
}

export function recordOpen(log: PetLog, now: number): PetLog {
  const at = Math.max(now, highWaterMark(log));
  return { ...log, sessions: [...log.sessions, { openAt: at, lastSeenAt: at }] };
}

/** Moves the latest session's last-seen time forward (never backward). Starts a session if there is none. */
export function recordHeartbeat(log: PetLog, now: number): PetLog {
  if (log.sessions.length === 0) return recordOpen(log, now);
  let latest = 0;
  log.sessions.forEach((s, i) => {
    if (s.openAt >= (log.sessions[latest] as Session).openAt) latest = i;
  });
  const sessions = log.sessions.map((s, i) => (i === latest ? { ...s, lastSeenAt: Math.max(s.lastSeenAt, now) } : { ...s }));
  return { ...log, sessions };
}
